import { Task as TaskIcon } from 'components/icons';
import DrawerPortal from 'components/drawer/DrawerPortal';
import { useCloseOnPressOutside } from 'components/drawer/useCloseOnPressOutside';
import useAuth from 'hooks/useAuth.ts';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { dispatch, useSelector } from 'store';
import { useGetTasksAddressedToEmployeeQuery } from 'store/api/tasks';
import { closeTemporaryDrawer, setNoBackdrop, toggleTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { TaskPriorityCode, TaskStatusType } from 'types/tasks';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { NotificationIconWrap } from './Notifications';
import StyledHeaderButton, { HeaderButtonCount } from './StyledHeaderButton';
import TasksDrawerContent from './Tasks/TasksDrawerContent';

const Tasks: React.FC = () => {
    const intl = useIntl();
    const isOpen = useSelector((state) => state.temporaryDrawer.activeDrawerId === 'tasks');
    const navigate = useNavigateWithReturnState();
    const { user } = useAuth();
    const userId = user?.id;

    // Refresh tasks every 10 min
    const { data: tasks, isLoading } = useGetTasksAddressedToEmployeeQuery(
        { employeeId: userId ?? 0, search: '' },
        { skip: typeof userId !== 'number', pollingInterval: 600000 }
    );

    // Filter tasks with status "not done"
    const notDoneTasks = tasks?.filter((task) => task.taskStatusTypeCode != TaskStatusType.DONE).length;

    // Filter tasks with priority "high"
    const highPriorityTasks = tasks?.filter((task) => task.taskPriorityTypeCode == TaskPriorityCode.HIGH).length;

    const toggleDrawer = () => {
        if (isOpen) dispatch(closeTemporaryDrawer());
        else {
            dispatch(toggleTemporaryDrawer('tasks'));
            dispatch(setNoBackdrop(true));
        }
    };

    // Without a backdrop the page keeps scrolling beside the drawer, so a press outside it closes it here.
    const triggerRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    useCloseOnPressOutside(isOpen, () => dispatch(closeTemporaryDrawer()), [triggerRef, contentRef]);

    const openAddressedTaskDetailsView = (taskId: number) => {
        dispatch(closeTemporaryDrawer());
        navigate(`/task/${taskId}`);
    };

    const totalNotificationsNumber = notDoneTasks ?? 0;
    const totalHighNotificationsNumber = highPriorityTasks ?? 0;

    return (
        <>
            <StyledHeaderButton
                ref={triggerRef}
                aria-label={intl.formatMessage({ id: 'tasks.tasks' })}
                onClick={toggleDrawer}
                disabled={isLoading}
            >
                {/* The dot carries the high-priority count the drawer breaks down; the header row
                    has no space for the number itself. */}
                <NotificationIconWrap hasUnread={totalHighNotificationsNumber > 0}>
                    <TaskIcon className="text-[16px]" />
                </NotificationIconWrap>
                <HeaderButtonCount count={isLoading ? undefined : totalNotificationsNumber} />
            </StyledHeaderButton>
            <DrawerPortal
                content={
                    // `contents`: the wrapper only marks the drawer's inside, and leaves the panel's grid alone.
                    <div ref={contentRef} className="contents">
                        <TasksDrawerContent
                            tasks={tasks ?? []}
                            openAddressTaskView={openAddressedTaskDetailsView}
                            toggleDrawer={toggleDrawer}
                        />
                    </div>
                }
                drawerId="tasks"
                applyColor="secondary"
                type="temporary"
                width={480}
                widensBelowSm
            />
        </>
    );
};

export default Tasks;
